import React, { useCallback, useEffect, useState } from "react";

import { useFormik } from "formik";
import * as Yup from "yup";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";

import { Add, Edit, Search } from "@mui/icons-material";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";

import PageContainer from "../../../components/container/PageContainer";
import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";

import { AddOrEditOrganization } from "../../../services/asaiadmin";

/* =========================================================
   BREADCRUMB
   ========================================================= */

const BCrumb = [
  {
    to: "/",
    title: "ASAI Admin",
  },
  {
    title: "Organization Master",
  },
];

/* =========================================================
   EMPTY FORM
   Active status is managed through the list Action column.
   ========================================================= */

const emptyForm = {
  id: 0,
  organizationCode: "",
  organizationName: "",
  description: "",
  isActive: true,
};

/* =========================================================
   RESPONSE HELPERS
   Supports camelCase and PascalCase API responses.
   ========================================================= */

const getResponseRows = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  return [];
};

const normalizeOrganization = (item) => {
  const activeValue = item.isActive ?? item.IsActive ?? true;

  return {
    id: item.id ?? item.Id ?? 0,

    organizationCode: item.organizationCode ?? item.OrganizationCode ?? "",

    organizationName: item.organizationName ?? item.OrganizationName ?? "",

    description: item.description ?? item.Description ?? "",

    isActive:
      activeValue === true ||
      activeValue === 1 ||
      activeValue === "1" ||
      activeValue === "true",
  };
};

const getResult = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) {
    return data[0] ?? {};
  }

  return data ?? {};
};

const getResultMessage = (result) => result?.message ?? result?.Message ?? "";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ??
  error?.response?.data?.Message ??
  error?.response?.data?.title ??
  error?.message ??
  fallback;

/* =========================================================
   ORGANIZATION MASTER
   ========================================================= */

const OrganizationMaster = () => {
  const [organizations, setOrganizations] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [pageError, setPageError] = useState("");

  /* =========================================================
     VALIDATION
     ========================================================= */

  const validationSchema = Yup.object({
    organizationCode: Yup.string()
      .trim()
      .required("Organization code is required.")
      .max(50, "Organization code cannot exceed 50 characters."),

    organizationName: Yup.string()
      .trim()
      .required("Organization name is required.")
      .max(150, "Organization name cannot exceed 150 characters."),

    description: Yup.string().max(
      500,
      "Description cannot exceed 500 characters.",
    ),

    isActive: Yup.boolean(),
  });

  /* =========================================================
     GET ORGANIZATIONS
     Action = GET
     ========================================================= */

  const getOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      setPageError("");

      const response = await AddOrEditOrganization({
        action: "GET",
      });

      const rows = getResponseRows(response).map(normalizeOrganization);

      setOrganizations(rows);
    } catch (error) {
      console.error("Error loading organizations:", error);

      setPageError(getErrorMessage(error, "Unable to load organizations."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getOrganizations();
  }, [getOrganizations]);

  /* =========================================================
     FORMIK
     ========================================================= */

  const formik = useFormik({
    initialValues: emptyForm,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: true,

    onSubmit: async (values, helpers) => {
      try {
        setSaving(true);

        const payload = {
          action: isEdit ? "UPDATE" : "INSERT",

          id: isEdit ? Number(values.id) : null,

          organizationCode: values.organizationCode.trim(),

          organizationName: values.organizationName.trim(),

          description: values.description.trim(),

          // New organizations are active by default.
          // Editing preserves the existing status.
          isActive: isEdit ? values.isActive : true,
        };

        console.log("Organization Payload:", payload);

        const response = await AddOrEditOrganization(payload);

        const result = getResult(response);

        console.log("Organization Response:", response);

        if (Number(result?.status ?? result?.Status) === 0) {
          toast.warning(
            getResultMessage(result) || "Unable to save organization.",
          );

          return;
        }

        toast.success(
          getResultMessage(result) ||
            (isEdit
              ? "Organization updated successfully."
              : "Organization added successfully."),
        );

        helpers.resetForm({
          values: emptyForm,
        });

        helpers.setTouched({});
        setIsEdit(false);
        setPage(0);

        await getOrganizations();
      } catch (error) {
        console.error("Error saving organization:", error);

        toast.error(getErrorMessage(error, "Unable to save organization."));
      } finally {
        setSaving(false);
        helpers.setSubmitting(false);
      }
    },
  });

  /* =========================================================
     RESET FORM
     ========================================================= */

  const resetForm = () => {
    formik.resetForm({
      values: emptyForm,
    });

    formik.setTouched({});
    setIsEdit(false);
  };

  /* =========================================================
     EDIT ORGANIZATION
     ========================================================= */

  const handleEdit = (item) => {
    const editData = {
      id: item.id,

      organizationCode: item.organizationCode || "",

      organizationName: item.organizationName || "",

      description: item.description || "",

      isActive: item.isActive,
    };

    formik.setValues(editData);
    formik.setTouched({});
    setIsEdit(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* =========================================================
     CHANGE ORGANIZATION STATUS
     Action = UPDATE
     Only Id and IsActive are submitted.
     ========================================================= */

  const handleStatusChange = async (item, nextStatus) => {
    try {
      const payload = {
        action: "UPDATE",
        id: item.id,
        isActive: nextStatus,
      };

      await AddOrEditOrganization(payload);

      toast.success(
        nextStatus
          ? "Organization activated successfully."
          : "Organization deactivated successfully.",
      );

      await getOrganizations();
    } catch (error) {
      console.error("Error updating organization status:", error);

      toast.error(
        getErrorMessage(error, "Unable to update organization status."),
      );
    }
  };

  /* =========================================================
     SEARCH AND STATUS FILTER
     ========================================================= */

  const filteredData = organizations.filter((item) => {
    const searchText = search.toLowerCase().trim();

    const matchesSearch =
      !searchText ||
      item.organizationCode.toLowerCase().includes(searchText) ||
      item.organizationName.toLowerCase().includes(searchText) ||
      item.description.toLowerCase().includes(searchText);

    const matchesStatus =
      statusFilter === "All" ||
      (statusFilter === "Active" && item.isActive) ||
      (statusFilter === "Inactive" && !item.isActive);

    return matchesSearch && matchesStatus;
  });

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <PageContainer
      title="Organization Master"
      description="Create and manage organizations"
    >
      <Breadcrumb title="Organization Master" items={BCrumb} />

      {/* =====================================================
          ADD / EDIT ORGANIZATION FORM
          ===================================================== */}

      <Card sx={{ p: 0, mb: 3 }}>
        <Box
          display="flex"
          alignItems="center"
          p={2}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <FeatherIcon icon="briefcase" width="18" />

          <Box sx={{ ml: 1 }}>Organization Master</Box>
        </Box>

        <CardContent sx={{ p: 3 }}>
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 600,
              color: "#111827",
              mb: 2,
            }}
          >
            {isEdit ? "Edit Organization" : "Add Organization"}
          </Typography>

          {pageError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setPageError("")}
            >
              {pageError}
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit} noValidate>
            <Grid container spacing={2}>
              {/* Organization Code */}

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Organization Code"
                  name="organizationCode"
                  value={formik.values.organizationCode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.organizationCode &&
                    Boolean(formik.errors.organizationCode)
                  }
                  helperText={
                    formik.touched.organizationCode
                      ? formik.errors.organizationCode
                      : ""
                  }
                  placeholder="e.g. ORG-001"
                  inputProps={{
                    maxLength: 50,
                  }}
                />
              </Grid>

              {/* Organization Name */}

              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Organization Name"
                  name="organizationName"
                  value={formik.values.organizationName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.organizationName &&
                    Boolean(formik.errors.organizationName)
                  }
                  helperText={
                    formik.touched.organizationName
                      ? formik.errors.organizationName
                      : ""
                  }
                  placeholder="Enter organization name"
                  inputProps={{
                    maxLength: 150,
                  }}
                />
              </Grid>

              {/* Description */}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={6}
                  size="small"
                  label="Description"
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                  helperText={
                    formik.touched.description ? formik.errors.description : ""
                  }
                  placeholder="Enter organization description"
                  inputProps={{
                    maxLength: 500,
                  }}
                />
              </Grid>

              {/* Active status is managed in the list Action column. */}

              {/* Form Buttons */}
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  // startIcon={
                  //   saving ? (
                  //     <CircularProgress size={16} color="inherit" />
                  //   ) : isEdit ? (
                  //     <Edit />
                  //   ) : (
                  //     <Add />
                  //   )
                  // }
                >
                  {saving ? "Saving..." : isEdit ? "Update" : "Add"}
                </Button>

                <Button
                  variant="outlined"
                  type="button"
                  onClick={resetForm}
                  disabled={saving}
                >
                  {isEdit ? "Cancel" : "Reset"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* =====================================================
          ORGANIZATION LIST
          ===================================================== */}

      <Card sx={{ p: 0 }}>
        <Box
          display="flex"
          alignItems="center"
          p={2}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <FeatherIcon icon="list" width="18" />

          <Box sx={{ ml: 1 }}>Organization List</Box>
        </Box>

        <CardContent>
          {/* Search and Status Filter */}

          <Grid container spacing={2} mb={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search organization code / name..."
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <Search
                      sx={{
                        mr: 1,
                        color: "text.secondary",
                      }}
                    />
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="organization-status-label">Status</InputLabel>

                <Select
                  labelId="organization-status-label"
                  value={statusFilter}
                  label="Status"
                  onChange={(event) => {
                    setStatusFilter(event.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid
              item
              xs={12}
              md={3}
              sx={{
                display: "flex",
                justifyContent: {
                  xs: "flex-start",
                  md: "flex-end",
                },
              }}
            >
              <Button
                variant="outlined"
                onClick={getOrganizations}
                disabled={loading}
                sx={{
                  textTransform: "none",
                }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>

          {/* Organization Table */}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>No.</b>
                  </TableCell>

                  <TableCell>
                    <b>Organization Code</b>
                  </TableCell>

                  <TableCell>
                    <b>Organization Name</b>
                  </TableCell>

                  <TableCell>
                    <b>Description</b>
                  </TableCell>

                  {/* Status and Edit share one Action column. */}

                  <TableCell align="center">
                    <b>Action</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No organizations found.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((item, index) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                      <TableCell>
                        <Typography fontWeight={600}>
                          {item.organizationCode}
                        </Typography>
                      </TableCell>

                      <TableCell>{item.organizationName}</TableCell>

                      <TableCell
                        sx={{
                          maxWidth: 350,
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {item.description || "-"}
                      </TableCell>

                      {/* ACTION: STATUS + EDIT */}

                      <TableCell align="center">
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          gap={1}
                        >
                          <Tooltip
                            title={item.isActive ? "Active" : "Inactive"}
                          >
                            <Switch
                              checked={item.isActive}
                              size="small"
                              onChange={(event) =>
                                handleStatusChange(item, event.target.checked)
                              }
                            />
                          </Tooltip>

                          <Tooltip title="Edit">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleEdit(item)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}

          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>
    </PageContainer>
  );
};

export default OrganizationMaster;
